"use client";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { RiCalendarEventFill } from "react-icons/ri";
import { Calendar } from "~/components/shadcn/Calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { cn } from "~/utils/fontsConfig";
import { useEventDetailsPageStore } from "~/state/store";
import { If } from "../../Conditional";
import TimeInput from "../../themed/TimeInput";
import { Label } from "../../shadcn/Label";
import { LockedSwitch } from "../../themed/LockedSwitch";
import { EventFormData } from "./EventFormDataType";
import { BASE_INPUT_STYLES } from "../../themed/BASE_STYLE_DEFINITIONS";
import { RouterOutputs } from "~/utils/api";
import { EventJourneyStatus } from "@prisma/client";
import { Button } from "../../themed/Button";
import { GoPlus } from "react-icons/go";
import { mergeToTime } from "~/utils/time";
import dynamic from "next/dynamic";

type Props = {
    event: EventBySlugOutput;
    permissions: RouterOutputs["permissions"]["getUserPermissions"];
};

const EventDateTime = ({ event, permissions }: Props) => {
    const { panelMode, setPanelMode } = useEventDetailsPageStore();
    const form = useFormContext();
    const [isLockedMode, setIsLockedMode] = useState(false);
    const EventTimezone = dynamic(() => import("./EventTimezone"), {
        ssr: false,
    });

    useEffect(() => {
        setIsLockedMode(false);
    }, [panelMode]);

    const startTime = DateTime.fromJSDate(event.startTime, {
        zone: "UTC",
    });
    const endTime =
        event.endTime &&
        DateTime.fromJSDate(event.endTime, {
            zone: "UTC",
        });
    const isEndDateDifferent = !!endTime && !startTime.hasSame(endTime, "day");
    const [isEndDateEditable, setIsEndDateEditable] = useState(
        isEndDateDifferent || false,
    );

    useEffect(() => {
        form.setValue(
            "isEndDateSeparate",
            isEndDateEditable || isEndDateDifferent,
            { shouldDirty: true },
        );
    }, [isEndDateEditable, isEndDateDifferent]);

    return (
        <div className="flex flex-row items-center gap-3">
            <If condition={panelMode !== "edit"}>
                <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-v3-N-500 p-2">
                    <RiCalendarEventFill aria-hidden={true} />
                </div>
            </If>

            <div className={"flex w-full flex-col"}>
                <div className="flex w-full flex-row">
                    <div
                        className={cn(
                            "flex w-full flex-col items-start",
                            panelMode === "edit" && "gap-2",
                        )}
                    >
                        <If condition={panelMode === "edit"}>
                            <div className="flex w-full flex-row">
                                <Label className="mb-1 grow text-left">
                                    Date
                                </Label>
                                <If
                                    condition={
                                        !!permissions.isAdmin ||
                                        !!permissions.isManager
                                    }
                                >
                                    <LockedSwitch
                                        tooltip="Set future recurrences with this same Start Time"
                                        hasValue={
                                            !!form.watch("startTimeLocked")
                                        }
                                        onCheckedChange={setIsLockedMode}
                                    />
                                </If>
                            </div>
                        </If>
                        <EventDate
                            isEditing={panelMode === "edit"}
                            isLockedMode={isLockedMode}
                            value="startTime"
                            valueLocked="startTimeLocked"
                            timeField="startTime24h"
                            label={
                                isEndDateDifferent || isEndDateEditable
                                    ? "Start"
                                    : undefined
                            }
                            onCustomizeEndDate={
                                !isEndDateEditable
                                    ? () => setIsEndDateEditable(true)
                                    : undefined
                            }
                        />
                        <If condition={isEndDateEditable || isEndDateDifferent}>
                            <EventDate
                                isEditing={panelMode === "edit"}
                                isLockedMode={isLockedMode}
                                value="endTime"
                                valueLocked="endTimeLocked"
                                timeField="endTime24h"
                                label="End"
                                isViewModeEnabled={false}
                            />
                        </If>
                    </div>
                </div>
                <div
                    className={cn(
                        "mt-1 flex gap-2",
                        panelMode === "edit" ? "flex-col" : "",
                    )}
                >
                    <div className="w-max">
                        <EventTime
                            className="text-sm"
                            isEditing={panelMode === "edit"}
                            event={event}
                            isLockedMode={isLockedMode}
                            isEndDateDifferent={isEndDateDifferent}
                        />
                    </div>
                    <EventTimezone />
                </div>
            </div>
        </div>
    );
};

export const EventDate = ({
    includeYear = true,
    isEditing,
    isLockedMode,
    value,
    valueLocked,
    timeField,
    label,
    isViewModeEnabled = true,
    onCustomizeEndDate,
}: {
    includeYear?: boolean;
    isEditing: boolean;
    isLockedMode: boolean;
    value: string;
    valueLocked: string;
    timeField: string;
    label?: string;
    isViewModeEnabled?: boolean;
    onCustomizeEndDate?: () => void;
}) => {
    const form = useFormContext();

    const [isOpen, setIsOpen] = useState(false);
    const time = form.watch(value);
    const timeLocked = form.watch(valueLocked);
    const [selectedTime, setSelectedTime] = useState<Date | null>(null);
    const [effectiveTime, setEffectiveTime] = useState(time || new Date());

    useEffect(() => {
        if (isLockedMode) {
            setEffectiveTime(timeLocked || new Date());
            return;
        }
        if (time) {
            setEffectiveTime(time);
            return;
        }

        if (value === "endTime" && time == null) {
            const startTime = form.watch("startTime");

            if (startTime) {
                const startDate = new Date(startTime);
                const endDate = new Date(
                    startDate.getTime() + 7 * 24 * 60 * 60 * 1000,
                );

                setEffectiveTime(endDate);
                return;
            }
        }

        setEffectiveTime(new Date());
    }, [time, timeLocked, isLockedMode]);

    const handleOpen = (open: boolean) => {
        if (!isEditing) return;
        setIsOpen(open);
    };

    return (
        <Popover open={isOpen} onOpenChange={handleOpen}>
            <div className="flex w-full flex-col md:flex-row md:items-center">
                <If condition={isEditing && !!label}>
                    <span className="w-[80px] shrink-0 text-start">
                        {label}
                    </span>
                </If>
                <div className="flex w-full flex-row items-center gap-2">
                    <PopoverTrigger
                        className={cn(
                            "text-s h-fit w-full flex-row items-center gap-x-2",
                            isEditing ? BASE_INPUT_STYLES : "",
                            "rounded-[10px]",
                        )}
                    >
                        <If condition={isEditing}>
                            <RiCalendarEventFill
                                className="text-v3-N-300"
                                size={20}
                            />
                        </If>
                        <If condition={isViewModeEnabled || isEditing}>
                            <div
                                className={cn(
                                    "flex w-full flex-wrap items-center gap-x-1 sm:flex-row",
                                )}
                            >
                                <div className="w-max break-keep">
                                    {Intl.DateTimeFormat("en-US", {
                                        timeZone: "UTC",
                                        weekday: "long",
                                    }).format(effectiveTime)}
                                </div>

                                <div>
                                    {Intl.DateTimeFormat("en-US", {
                                        timeZone: "UTC",
                                        month: "long",
                                        day: "numeric",
                                    }).format(effectiveTime)}
                                    {includeYear
                                        ? `, ${effectiveTime.getFullYear()}`
                                        : ""}
                                </div>
                            </div>
                        </If>
                    </PopoverTrigger>
                    {isEditing && onCustomizeEndDate && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="w-[40px] shrink-0 rounded-md border border-v3-N-500 bg-v3-N-500"
                            onClick={onCustomizeEndDate}
                        >
                            <GoPlus className="h-4 w-4" />
                            <span className="sr-only">Customize End Date</span>
                        </Button>
                    )}
                </div>
            </div>
            <PopoverContent className="w-auto p-0">
                <Calendar
                    className={cn("rounded-md bg-v3-N-500")}
                    mode="single"
                    selected={selectedTime || effectiveTime}
                    onSelect={(calDate) => {
                        if (!calDate) return;
                        setSelectedTime(calDate);
                        let utcDate = DateTime.fromJSDate(calDate, {
                            zone: "local",
                        })
                            .toUTC(0, { keepLocalTime: true })
                            .toJSDate();
                        const formValue = isLockedMode ? valueLocked : value;
                        // incorporate time to so ui does not display
                        // wrong time during the update
                        utcDate = mergeToTime(
                            form.getValues(timeField),
                            utcDate,
                        );
                        form.setValue(formValue, utcDate, {
                            shouldDirty: true,
                        });
                    }}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
};

export const EventTime = ({
    isEditing,
    className,
    event,
    isLockedMode,
    isEndDateDifferent = false,
}: {
    isEditing: boolean;
    className?: string;
    event: EventBySlugOutput;
    isLockedMode: boolean;
    isEndDateDifferent?: boolean;
}) => {
    const form = useFormContext<EventFormData>();

    if (event.isMembersOnly) {
        if (!event.currentUserIsMemberOfEventCommunity) {
            return null;
        }
    }

    return (
        <div
            className={cn(
                isEditing ? "mt-4 max-w-[360px]" : "",
                "flex w-full flex-row items-center space-x-2",
                className,
            )}
        >
            <div className="flex w-full flex-col">
                <If condition={isEditing}>
                    <div className="flex w-full flex-row">
                        <Label className="mb-1 grow text-left">Time</Label>
                    </div>
                </If>

                <div
                    className={cn(
                        "flex w-full gap-2",
                        isEditing ? "flex-col" : "",
                    )}
                >
                    <If condition={isLockedMode}>
                        <div className="flex w-full flex-col sm:flex-row sm:gap-4">
                            <If condition={isEditing}>
                                <Label className="flex w-[80px] items-center text-[16px]">
                                    Start
                                </Label>
                            </If>
                            <TimeInput
                                isEditMode={isEditing}
                                className="w-full"
                                time={form.watch("startTimeLocked")}
                                formField={"startTime24hLocked"}
                            />
                        </div>
                        <div className="flex w-full flex-col sm:flex-row sm:gap-4">
                            <If condition={isEditing}>
                                <Label className="flex w-[80px] items-center text-[16px]">
                                    End
                                </Label>
                            </If>
                            <TimeInput
                                isEditMode={isEditing}
                                className="w-full"
                                time={form.watch("endTimeLocked")}
                                formField={"endTime24hLocked"}
                            />
                        </div>
                    </If>

                    <If condition={!isLockedMode}>
                        <If condition={!event.isTimeSet && !isEditing}>
                            <div className="w-full">TBD</div>
                        </If>
                        <If condition={event.isTimeSet || isEditing}>
                            <div
                                className={cn(
                                    "flex w-full flex-col sm:flex-row sm:gap-4",
                                    isEditing
                                        ? "border-b border-v3-neutral py-2 sm:items-center"
                                        : "w-max",
                                )}
                            >
                                <If condition={isEditing}>
                                    <Label className="flex w-[80px] text-[16px]">
                                        Start
                                    </Label>
                                </If>
                                <TimeInput
                                    isEditMode={isEditing}
                                    timeZone={event.timezone}
                                    time={
                                        event.isTimeSet
                                            ? form.watch("startTime")
                                            : null
                                    }
                                    formField={"startTime24h"}
                                    className={"w-full"}
                                    required={
                                        event.eventJourneyStatus ===
                                        EventJourneyStatus.DRAFT_PROPOSED
                                    }
                                />
                            </div>

                            <If
                                condition={
                                    !isEditing && !!form.watch("endTime")
                                }
                            >
                                <div className="mx-1">-</div>
                                {event.endTime && isEndDateDifferent && (
                                    <span>
                                        {Intl.DateTimeFormat("en-US", {
                                            timeZone: "UTC",
                                            month: "short",
                                            day: "numeric",
                                        }).format(event.endTime)}
                                    </span>
                                )}
                            </If>

                            <If
                                condition={!!form.watch("endTime") || isEditing}
                            >
                                <div
                                    className={cn(
                                        "flex w-full flex-col sm:flex-row sm:gap-4",
                                        isEditing
                                            ? "border-b border-v3-neutral pb-2"
                                            : "w-max",
                                    )}
                                >
                                    <If condition={isEditing}>
                                        <Label className="flex w-[80px] items-center text-[16px]">
                                            End
                                        </Label>
                                    </If>
                                    <TimeInput
                                        isEditMode={isEditing}
                                        timeZone={event.timezone}
                                        time={
                                            event.isTimeSet
                                                ? form.watch("endTime")
                                                : null
                                        }
                                        className={"w-full"}
                                        formField={"endTime24h"}
                                        minTime={form.watch("startTime24h")}
                                    />
                                </div>
                            </If>
                        </If>
                    </If>
                </div>
            </div>
        </div>
    );
};

export default EventDateTime;
